import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { InquiryMgmtStateType, ApiGetDataStatus } from '@redux/types'
import {
	InquiryType,
	InquiryCommentType,
	InquiryStatusLogType,
	InquiryStatusType,
} from '@schemas'

const initialState: InquiryMgmtStateType = {
	// triggers
	triggerInitInquiryMgmt: false,
	triggerLoadInquiryDetail: false,
	triggerUpdateStatus: false,
	triggerUpdateHandler: false,
	triggerAddComment: false,
	triggerDeleteComment: false,
	triggerDeleteInquiry: false,

	// list
	statusFilter: 'all',
	inquiryList: [],
	inquiryListStatus: 'init',

	// detail
	selectedInquiry: null,
	selectedInquiryStatus: 'init',
	comments: [],
	statusLogs: [],

	// user list
	userList: [],

	// payloads
	updateStatusPayload: { inquiryId: '', status: '', note: '' },
	updateHandlerPayload: { inquiryId: '', handlerId: '' },
	addCommentPayload: { inquiryId: '', body: '', isInternal: true },
	deleteCommentPayload: { inquiryId: '', commentId: '' },
	deleteInquiryId: '',
}

const slice = createSlice({
	name: 'inquiryMgmt',
	initialState,
	reducers: {
		// triggers
		TRIGGER_INIT_INQUIRY_MGMT: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerInitInquiryMgmt = action.payload
		},
		TRIGGER_LOAD_INQUIRY_DETAIL: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerLoadInquiryDetail = action.payload
		},
		TRIGGER_UPDATE_STATUS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerUpdateStatus = action.payload
		},
		TRIGGER_UPDATE_HANDLER: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerUpdateHandler = action.payload
		},
		TRIGGER_ADD_COMMENT: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerAddComment = action.payload
		},
		TRIGGER_DELETE_COMMENT: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerDeleteComment = action.payload
		},
		TRIGGER_DELETE_INQUIRY: (
			state: InquiryMgmtStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerDeleteInquiry = action.payload
		},

		// list
		STATUS_FILTER: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryStatusType | 'all'>,
		) => {
			state.statusFilter = action.payload
		},
		INQUIRY_LIST: (state: InquiryMgmtStateType, action: PayloadAction<InquiryType[]>) => {
			state.inquiryList = action.payload
		},
		INQUIRY_LIST_STATUS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.inquiryListStatus = action.payload
		},

		// detail
		SELECTED_INQUIRY: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryType | null>,
		) => {
			state.selectedInquiry = action.payload
		},
		SELECTED_INQUIRY_STATUS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.selectedInquiryStatus = action.payload
		},
		COMMENTS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryCommentType[]>,
		) => {
			state.comments = action.payload
		},
		STATUS_LOGS: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryStatusLogType[]>,
		) => {
			state.statusLogs = action.payload
		},

		// user list
		USER_LIST: (
			state: InquiryMgmtStateType,
			action: PayloadAction<{ id: string; name: string }[]>,
		) => {
			state.userList = action.payload
		},

		// payloads
		UPDATE_STATUS_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['updateStatusPayload']>,
		) => {
			state.updateStatusPayload = action.payload
		},
		UPDATE_HANDLER_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['updateHandlerPayload']>,
		) => {
			state.updateHandlerPayload = action.payload
		},
		ADD_COMMENT_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['addCommentPayload']>,
		) => {
			state.addCommentPayload = action.payload
		},
		DELETE_COMMENT_PAYLOAD: (
			state: InquiryMgmtStateType,
			action: PayloadAction<InquiryMgmtStateType['deleteCommentPayload']>,
		) => {
			state.deleteCommentPayload = action.payload
		},
		DELETE_INQUIRY_ID: (state: InquiryMgmtStateType, action: PayloadAction<string>) => {
			state.deleteInquiryId = action.payload
		},
	},
})

export const inquiryMgmtActions = slice.actions
export default slice.reducer
